// 🔥 가격 복구 마법사 컴포넌트 - 5단계 프리미엄급 Wizard
// [배경] 스냅샷 선택 → 복구 범위 → Diff 미리보기 → 데이터 검증 → 최종 확인/실행
// 안전하고 직관적인 복구 프로세스 제공 - 프리미엄 UI/UX

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Stack,
  Alert,
  AlertTitle,
  CircularProgress,
  LinearProgress,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
  IconButton,
  alpha,
  Tooltip,
  Badge
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Restore as RestoreIcon,
  Storage as StorageIcon,
  FilterAlt as FilterIcon,
  Compare as CompareIcon,
  VerifiedUser as ValidateIcon,
  PlayArrow as ExecuteIcon,
  Close as CloseIcon,
  Smartphone as SmartphoneIcon,
  Tablet as TabletIcon,
  Headphones as HeadphonesIcon,
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  AutoAwesome as SparkleIcon,
  Security as SecurityIcon,
  Timeline as TimelineIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  SwapHoriz as SwapIcon,
  DataObject as DataIcon,
  Shield as ShieldIcon,
  Bolt as BoltIcon
} from '@mui/icons-material';
import { Timestamp } from 'firebase/firestore';

import { PriceSnapshotService } from '../../../../services/priceSnapshotService';
import {
  PriceSnapshot,
  SnapshotFilter,
  PriceDiffResult,
  ValidationResult,
  RestoreOptions,
  RestoreProgress,
  RestoreResult,
  WIZARD_STEPS,
  WizardStep
} from '../../../../types/priceSnapshot';
import { DeviceType, DeviceBrand } from '../../../../types/deviceCatalog';
import PriceDiffPreview from './PriceDiffPreview';

// ============================================================
// 📊 타입 정의
// ============================================================

interface PriceRestoreWizardProps {
  open: boolean;
  snapshot: PriceSnapshot;
  adminId: string;
  onClose: (restored: boolean) => void;
}

// ============================================================
// 🎨 프리미엄 스타일 상수
// ============================================================

const PREMIUM_COLORS = {
  primary: {
    main: '#6366f1',
    light: '#818cf8',
    dark: '#4f46e5',
    gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
  },
  success: {
    main: '#10b981',
    light: '#34d399',
    gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
  },
  warning: {
    main: '#f59e0b',
    light: '#fbbf24',
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
  },
  error: {
    main: '#ef4444',
    light: '#f87171',
    gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
  },
  glass: {
    background: 'rgba(255, 255, 255, 0.9)',
    border: 'rgba(255, 255, 255, 0.2)',
    shadow: '0 8px 32px rgba(31, 38, 135, 0.1)'
  }
};

const STEP_CONFIG: Record<WizardStep, { icon: React.ReactNode; color: string; gradient: string }> = {
  select_snapshot: { 
    icon: <StorageIcon />, 
    color: '#6366f1',
    gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
  },
  select_scope: { 
    icon: <FilterIcon />, 
    color: '#8b5cf6',
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)'
  },
  diff_preview: { 
    icon: <CompareIcon />, 
    color: '#06b6d4',
    gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)'
  },
  validation: { 
    icon: <ValidateIcon />, 
    color: '#10b981',
    gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
  },
  confirm_restore: { 
    icon: <ExecuteIcon />, 
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
  }
};

// ============================================================
// 🔧 유틸리티 함수
// ============================================================

const formatDate = (date: Timestamp | Date | string): string => {
  const d = date instanceof Timestamp ? date.toDate() : new Date(date);
  return d.toLocaleString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// ============================================================
// 🎨 커스텀 Stepper 컴포넌트
// ============================================================

interface PremiumStepperProps {
  activeStep: number;
  steps: typeof WIZARD_STEPS;
  isMobile: boolean;
}

const PremiumStepper: React.FC<PremiumStepperProps> = ({ activeStep, steps, isMobile }) => {
  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'flex-start' : 'center',
      justifyContent: 'space-between',
      gap: isMobile ? 1 : 0,
      mb: 4
    }}>
      {steps.map((step, index) => {
        const config = STEP_CONFIG[step.step];
        const isActive = index === activeStep;
        const isCompleted = index < activeStep;
        
        return (
          <React.Fragment key={step.step}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: 1.5,
              flex: isMobile ? 'none' : 1,
              justifyContent: isMobile ? 'flex-start' : 'center'
            }}>
              {/* Step Circle */}
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: isActive ? config.gradient : isCompleted ? PREMIUM_COLORS.success.gradient : '#e5e7eb',
                  color: isActive || isCompleted ? '#fff' : '#9ca3af',
                  boxShadow: isActive ? `0 8px 24px ${alpha(config.color, 0.4)}` : 'none',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: isActive ? 'scale(1.1)' : 'scale(1)',
                  position: 'relative',
                  '&::before': isActive ? {
                    content: '""',
                    position: 'absolute',
                    inset: -4,
                    borderRadius: '50%',
                    border: `2px solid ${alpha(config.color, 0.3)}`,
                    animation: 'pulse 2s ease-in-out infinite'
                  } : {}
                }}
              >
                {isCompleted ? <CheckIcon /> : config.icon}
              </Box>
              
              {/* Step Label */}
              <Box sx={{ display: isMobile ? 'block' : { xs: 'none', lg: 'block' } }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: isActive ? 700 : 500,
                    color: isActive ? config.color : isCompleted ? PREMIUM_COLORS.success.main : '#6b7280',
                    transition: 'all 0.3s ease',
                    letterSpacing: isActive ? '0.02em' : 'normal'
                  }}
                >
                  {step.label}
                </Typography>
                {!isMobile && (
                  <Typography
                    variant="caption"
                    sx={{ color: '#9ca3af', display: 'block' }}
                  >
                    {step.description}
                  </Typography>
                )}
              </Box>
            </Box>
            
            {/* Connector Line */}
            {index < steps.length - 1 && !isMobile && (
              <Box sx={{ 
                flex: 0.5, 
                height: 2, 
                mx: 1,
                background: index < activeStep 
                  ? PREMIUM_COLORS.success.gradient 
                  : '#e5e7eb',
                borderRadius: 1,
                transition: 'all 0.5s ease'
              }} />
            )}
          </React.Fragment>
        );
      })}
      
      {/* Pulse Animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.6; transform: scale(1); }
          50% { opacity: 0.3; transform: scale(1.1); }
        }
      `}</style>
    </Box>
  );
};

// ============================================================
// 🎨 프리미엄 카드 컴포넌트
// ============================================================

interface GlassCardProps {
  children: React.ReactNode;
  gradient?: string;
  sx?: object;
  hover?: boolean;
  onClick?: () => void;
  selected?: boolean;
}

const GlassCard: React.FC<GlassCardProps> = ({ 
  children, 
  gradient, 
  sx = {}, 
  hover = true,
  onClick,
  selected = false
}) => (
  <Card
    onClick={onClick}
    sx={{
      background: gradient || 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(20px)',
      border: selected ? '2px solid' : '1px solid',
      borderColor: selected ? PREMIUM_COLORS.primary.main : 'rgba(255, 255, 255, 0.3)',
      borderRadius: 3,
      boxShadow: selected 
        ? `0 12px 40px ${alpha(PREMIUM_COLORS.primary.main, 0.25)}` 
        : '0 4px 24px rgba(0, 0, 0, 0.06)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: onClick ? 'pointer' : 'default',
      overflow: 'hidden',
      position: 'relative',
      ...(hover && onClick && {
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
          borderColor: alpha(PREMIUM_COLORS.primary.main, 0.5)
        }
      }),
      ...sx
    }}
  >
    {children}
  </Card>
);

// ============================================================
// 📦 메인 컴포넌트
// ============================================================

const PriceRestoreWizard: React.FC<PriceRestoreWizardProps> = ({
  open,
  snapshot,
  adminId,
  onClose
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ============================================================
  // 📊 상태 관리
  // ============================================================

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 2: 복구 범위
  const [filter, setFilter] = useState<SnapshotFilter>({
    brand: 'all',
    deviceType: 'all'
  });

  // Step 3: Diff 결과
  const [diffResult, setDiffResult] = useState<PriceDiffResult | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);

  // Step 4: 검증 결과
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [validationLoading, setValidationLoading] = useState(false);

  // Step 5: 복구 옵션
  const [createBackupBefore, setCreateBackupBefore] = useState(true);
  const [restoreMode, setRestoreMode] = useState<'replace' | 'merge'>('replace');

  // 복구 진행 상태
  const [restoring, setRestoring] = useState(false);
  const [restoreProgress, setRestoreProgress] = useState<RestoreProgress | null>(null);
  const [restoreResult, setRestoreResult] = useState<RestoreResult | null>(null);

  // ============================================================
  // 🔄 스텝 이동
  // ============================================================

  const handleNext = async () => {
    setError(null);

    if (activeStep === 1) {
      await loadDiff();
    } else if (activeStep === 2) {
      await loadValidation();
    }

    setActiveStep(prev => Math.min(prev + 1, WIZARD_STEPS.length - 1));
  };

  const handleBack = () => {
    setError(null);
    setActiveStep(prev => Math.max(prev - 1, 0));
  };

  // ============================================================
  // 📊 Diff 계산
  // ============================================================

  const loadDiff = async () => {
    setDiffLoading(true);
    setError(null);

    try {
      const result = await PriceSnapshotService.compareWithCurrent(snapshot.id, filter);
      setDiffResult(result);
    } catch (e) {
      console.error('Diff 계산 실패:', e);
      setError('변경사항 비교에 실패했습니다.');
    } finally {
      setDiffLoading(false);
    }
  };

  // ============================================================
  // ✅ 검증 실행
  // ============================================================

  const loadValidation = async () => {
    setValidationLoading(true);
    setError(null);

    try {
      const snapshotData = await PriceSnapshotService.getSnapshotData(snapshot.id, filter);
      const result = PriceSnapshotService.validatePriceData(snapshotData);
      setValidationResult(result);
    } catch (e) {
      console.error('검증 실패:', e);
      setError('데이터 검증에 실패했습니다.');
    } finally {
      setValidationLoading(false);
    }
  };

  // ============================================================
  // 🔄 복구 실행
  // ============================================================

  const handleRestore = async () => {
    setRestoring(true);
    setError(null);
    setRestoreResult(null);

    try {
      const options: RestoreOptions = {
        mode: restoreMode,
        filter,
        createBackupBefore,
        skipValidation: false,
        dryRun: false
      };

      const result = await PriceSnapshotService.restoreFromSnapshot(
        snapshot.id,
        options,
        (progress) => setRestoreProgress(progress)
      );

      setRestoreResult(result);

      if (result.success) {
        setTimeout(() => {
          onClose(true);
        }, 2000);
      }

    } catch (e) {
      console.error('복구 실패:', e);
      setError(e instanceof Error ? e.message : '복구에 실패했습니다.');
    } finally {
      setRestoring(false);
    }
  };

  const handleClose = () => {
    if (!restoring) {
      onClose(!!restoreResult?.success);
    }
  };

  // ============================================================
  // 🎨 Step 1: 스냅샷 확인 (프리미엄 UI)
  // ============================================================

  const renderStep1_SnapshotInfo = () => (
    <Fade in timeout={500}>
      <Box>
        {/* 헤더 섹션 */}
        <Box sx={{ 
          background: PREMIUM_COLORS.primary.gradient,
          borderRadius: 3,
          p: 3,
          mb: 3,
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)'
          }} />
          <Box sx={{
            position: 'absolute',
            bottom: -30,
            left: '30%',
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.05)'
          }} />
          
          <Stack direction="row" alignItems="center" spacing={2} sx={{ position: 'relative' }}>
            <Box sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <RestoreIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
                {snapshot.description}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {formatDate(snapshot.createdAt)} 생성
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* 통계 카드 그리드 */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <GlassCard sx={{ height: '100%' }}>
              <CardContent sx={{ textAlign: 'center', py: 2.5 }}>
                <Box sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  background: alpha(PREMIUM_COLORS.primary.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 1.5
                }}>
                  <DataIcon sx={{ color: PREMIUM_COLORS.primary.main }} />
                </Box>
                <Typography variant="h4" fontWeight={700} color="primary">
                  {snapshot.totalPrices.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  총 가격 데이터
                </Typography>
              </CardContent>
            </GlassCard>
          </Grid>

          <Grid item xs={6} md={3}>
            <GlassCard sx={{ height: '100%' }}>
              <CardContent sx={{ textAlign: 'center', py: 2.5 }}>
                <Box sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  background: alpha(PREMIUM_COLORS.success.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 1.5
                }}>
                  <ShieldIcon sx={{ color: PREMIUM_COLORS.success.main }} />
                </Box>
                <Chip
                  size="small"
                  icon={snapshot.validationStatus === 'valid' ? <CheckIcon /> : <WarningIcon />}
                  label={snapshot.validationStatus === 'valid' ? '검증됨' : snapshot.validationStatus}
                  color={snapshot.validationStatus === 'valid' ? 'success' : 'warning'}
                  sx={{ fontWeight: 600 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  검증 상태
                </Typography>
              </CardContent>
            </GlassCard>
          </Grid>

          <Grid item xs={6} md={3}>
            <GlassCard sx={{ height: '100%' }}>
              <CardContent sx={{ textAlign: 'center', py: 2.5 }}>
                <Box sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  background: alpha('#06b6d4', 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 1.5
                }}>
                  <StorageIcon sx={{ color: '#06b6d4' }} />
                </Box>
                <Typography variant="h4" fontWeight={700} sx={{ color: '#06b6d4' }}>
                  {snapshot.chunkCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  데이터 청크
                </Typography>
              </CardContent>
            </GlassCard>
          </Grid>

          <Grid item xs={6} md={3}>
            <GlassCard sx={{ height: '100%' }}>
              <CardContent sx={{ textAlign: 'center', py: 2.5 }}>
                <Box sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  background: alpha('#f59e0b', 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 1.5
                }}>
                  <BoltIcon sx={{ color: '#f59e0b' }} />
                </Box>
                <Typography variant="h4" fontWeight={700} sx={{ color: '#f59e0b' }}>
                  v{snapshot.version}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  스냅샷 버전
                </Typography>
              </CardContent>
            </GlassCard>
          </Grid>
        </Grid>

        {/* 디바이스 분포 */}
        <GlassCard>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <SparkleIcon sx={{ color: PREMIUM_COLORS.primary.main }} />
              <Typography variant="subtitle1" fontWeight={600}>
                브랜드/디바이스별 분포
              </Typography>
            </Stack>
            
            <Grid container spacing={2}>
              {/* Apple */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ 
                  p: 2, 
                  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                  borderRadius: 2
                }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <Box sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1,
                      background: '#000',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>A</Typography>
                    </Box>
                    <Typography variant="subtitle2" fontWeight={600}>Apple</Typography>
                    <Chip 
                      size="small" 
                      label={
                        (snapshot.breakdown['Apple-smartphone'] || 0) +
                        (snapshot.breakdown['Apple-tablet'] || 0) +
                        (snapshot.breakdown['Apple-wearable'] || 0)
                      }
                      sx={{ ml: 'auto', fontWeight: 600 }}
                    />
                  </Stack>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      icon={<SmartphoneIcon sx={{ fontSize: 16 }} />}
                      label={`스마트폰 ${snapshot.breakdown['Apple-smartphone'] || 0}`}
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      icon={<TabletIcon sx={{ fontSize: 16 }} />}
                      label={`태블릿 ${snapshot.breakdown['Apple-tablet'] || 0}`}
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      icon={<HeadphonesIcon sx={{ fontSize: 16 }} />}
                      label={`웨어러블 ${snapshot.breakdown['Apple-wearable'] || 0}`}
                      variant="outlined"
                    />
                  </Stack>
                </Paper>
              </Grid>

              {/* Samsung */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ 
                  p: 2, 
                  background: 'linear-gradient(135deg, #e8f4fd 0%, #d1e9fc 100%)',
                  borderRadius: 2
                }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <Box sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1,
                      background: '#1428a0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>S</Typography>
                    </Box>
                    <Typography variant="subtitle2" fontWeight={600}>Samsung</Typography>
                    <Chip 
                      size="small" 
                      label={
                        (snapshot.breakdown['Samsung-smartphone'] || 0) +
                        (snapshot.breakdown['Samsung-tablet'] || 0)
                      }
                      color="primary"
                      sx={{ ml: 'auto', fontWeight: 600 }}
                    />
                  </Stack>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      icon={<SmartphoneIcon sx={{ fontSize: 16 }} />}
                      label={`스마트폰 ${snapshot.breakdown['Samsung-smartphone'] || 0}`}
                      variant="outlined"
                      color="primary"
                    />
                    <Chip
                      size="small"
                      icon={<TabletIcon sx={{ fontSize: 16 }} />}
                      label={`태블릿 ${snapshot.breakdown['Samsung-tablet'] || 0}`}
                      variant="outlined"
                      color="primary"
                    />
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </GlassCard>
      </Box>
    </Fade>
  );

  // ============================================================
  // 🎨 Step 2: 복구 범위 선택 (프리미엄 UI)
  // ============================================================

  const renderStep2_SelectScope = () => (
    <Fade in timeout={500}>
      <Box>
        <Alert 
          severity="info" 
          sx={{ 
            mb: 3, 
            borderRadius: 2,
            '& .MuiAlert-icon': { alignItems: 'center' }
          }}
          icon={<FilterIcon />}
        >
          복구할 데이터의 범위를 선택하세요. 전체 또는 특정 브랜드/디바이스만 선택할 수 있습니다.
        </Alert>

        <Grid container spacing={3}>
          {/* 브랜드 선택 */}
          <Grid item xs={12} md={6}>
            <GlassCard>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <SparkleIcon sx={{ color: PREMIUM_COLORS.primary.main }} />
                  <Typography variant="subtitle1" fontWeight={700}>
                    브랜드 선택
                  </Typography>
                </Stack>
                
                <Stack spacing={1.5}>
                  {(['all', 'Apple', 'Samsung'] as const).map((brand) => {
                    const isSelected = filter.brand === brand;
                    const count = brand === 'all' 
                      ? snapshot.totalPrices
                      : brand === 'Apple'
                        ? (snapshot.breakdown['Apple-smartphone'] || 0) +
                          (snapshot.breakdown['Apple-tablet'] || 0) +
                          (snapshot.breakdown['Apple-wearable'] || 0)
                        : (snapshot.breakdown['Samsung-smartphone'] || 0) +
                          (snapshot.breakdown['Samsung-tablet'] || 0);

                    return (
                      <Paper
                        key={brand}
                        onClick={() => setFilter({ ...filter, brand })}
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          border: '2px solid',
                          borderColor: isSelected ? PREMIUM_COLORS.primary.main : 'transparent',
                          background: isSelected 
                            ? alpha(PREMIUM_COLORS.primary.main, 0.05)
                            : '#f9fafb',
                          borderRadius: 2,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: alpha(PREMIUM_COLORS.primary.main, 0.5),
                            transform: 'translateX(4px)'
                          }
                        }}
                      >
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Radio 
                              checked={isSelected} 
                              sx={{ 
                                p: 0,
                                '&.Mui-checked': { color: PREMIUM_COLORS.primary.main }
                              }} 
                            />
                            <Typography fontWeight={isSelected ? 600 : 500}>
                              {brand === 'all' ? '전체 브랜드' : brand}
                            </Typography>
                          </Stack>
                          <Chip 
                            size="small" 
                            label={count.toLocaleString()}
                            color={isSelected ? 'primary' : 'default'}
                            sx={{ fontWeight: 600 }}
                          />
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              </CardContent>
            </GlassCard>
          </Grid>

          {/* 디바이스 타입 선택 */}
          <Grid item xs={12} md={6}>
            <GlassCard>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <SmartphoneIcon sx={{ color: PREMIUM_COLORS.primary.main }} />
                  <Typography variant="subtitle1" fontWeight={700}>
                    디바이스 타입
                  </Typography>
                </Stack>
                
                <Stack spacing={1.5}>
                  {([
                    { value: 'all', label: '전체 디바이스', icon: <DataIcon /> },
                    { value: 'smartphone', label: '스마트폰', icon: <SmartphoneIcon /> },
                    { value: 'tablet', label: '태블릿', icon: <TabletIcon /> },
                    { value: 'wearable', label: '웨어러블', icon: <HeadphonesIcon /> }
                  ] as const).map((item) => {
                    const isSelected = filter.deviceType === item.value;

                    return (
                      <Paper
                        key={item.value}
                        onClick={() => setFilter({ ...filter, deviceType: item.value })}
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          border: '2px solid',
                          borderColor: isSelected ? PREMIUM_COLORS.primary.main : 'transparent',
                          background: isSelected 
                            ? alpha(PREMIUM_COLORS.primary.main, 0.05)
                            : '#f9fafb',
                          borderRadius: 2,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: alpha(PREMIUM_COLORS.primary.main, 0.5),
                            transform: 'translateX(4px)'
                          }
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Radio 
                            checked={isSelected} 
                            sx={{ 
                              p: 0,
                              '&.Mui-checked': { color: PREMIUM_COLORS.primary.main }
                            }} 
                          />
                          <Box sx={{ 
                            color: isSelected ? PREMIUM_COLORS.primary.main : '#6b7280',
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            {item.icon}
                          </Box>
                          <Typography fontWeight={isSelected ? 600 : 500}>
                            {item.label}
                          </Typography>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              </CardContent>
            </GlassCard>
          </Grid>
        </Grid>

        {/* 선택 요약 */}
        <Box sx={{ 
          mt: 3, 
          p: 2.5, 
          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
          borderRadius: 2,
          border: '1px solid',
          borderColor: alpha('#0ea5e9', 0.2)
        }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <CheckIcon sx={{ color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                선택된 복구 범위
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {filter.brand === 'all' ? '전체 브랜드' : filter.brand}
                {' · '}
                {filter.deviceType === 'all' ? '전체 디바이스' : 
                  filter.deviceType === 'smartphone' ? '스마트폰' : 
                  filter.deviceType === 'tablet' ? '태블릿' : '웨어러블'}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Box>
    </Fade>
  );

  // ============================================================
  // 🎨 Step 3: Diff 미리보기 (프리미엄 UI)
  // ============================================================

  const renderStep3_DiffPreview = () => (
    <Fade in timeout={500}>
      <Box>
        {diffLoading ? (
          <Box sx={{ 
            textAlign: 'center', 
            py: 8,
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            borderRadius: 3
          }}>
            <Box sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: PREMIUM_COLORS.primary.gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
              boxShadow: `0 8px 32px ${alpha(PREMIUM_COLORS.primary.main, 0.3)}`
            }}>
              <CircularProgress size={40} sx={{ color: 'white' }} />
            </Box>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              변경사항 분석 중...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              현재 데이터와 스냅샷을 비교하고 있습니다
            </Typography>
          </Box>
        ) : diffResult ? (
          <>
            {/* Diff 요약 카드 */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <GlassCard sx={{ background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)' }}>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <TrendingUpIcon sx={{ color: PREMIUM_COLORS.success.main, fontSize: 28, mb: 1 }} />
                    <Typography variant="h4" fontWeight={700} color="success.main">
                      {diffResult.summary.added}
                    </Typography>
                    <Typography variant="caption" fontWeight={500}>추가됨</Typography>
                  </CardContent>
                </GlassCard>
              </Grid>
              <Grid item xs={6} sm={3}>
                <GlassCard sx={{ background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)' }}>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <TrendingDownIcon sx={{ color: PREMIUM_COLORS.error.main, fontSize: 28, mb: 1 }} />
                    <Typography variant="h4" fontWeight={700} color="error.main">
                      {diffResult.summary.removed}
                    </Typography>
                    <Typography variant="caption" fontWeight={500}>삭제됨</Typography>
                  </CardContent>
                </GlassCard>
              </Grid>
              <Grid item xs={6} sm={3}>
                <GlassCard sx={{ background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' }}>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <SwapIcon sx={{ color: PREMIUM_COLORS.warning.main, fontSize: 28, mb: 1 }} />
                    <Typography variant="h4" fontWeight={700} color="warning.main">
                      {diffResult.summary.modified}
                    </Typography>
                    <Typography variant="caption" fontWeight={500}>변경됨</Typography>
                  </CardContent>
                </GlassCard>
              </Grid>
              <Grid item xs={6} sm={3}>
                <GlassCard sx={{ background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)' }}>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <CheckIcon sx={{ color: '#6b7280', fontSize: 28, mb: 1 }} />
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#6b7280' }}>
                      {diffResult.summary.unchanged}
                    </Typography>
                    <Typography variant="caption" fontWeight={500}>동일</Typography>
                  </CardContent>
                </GlassCard>
              </Grid>
            </Grid>

            <PriceDiffPreview diffResult={diffResult} compact />
          </>
        ) : (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            변경사항을 불러올 수 없습니다. 이전 단계로 돌아가 다시 시도해주세요.
          </Alert>
        )}
      </Box>
    </Fade>
  );

  // ============================================================
  // 🎨 Step 4: 데이터 검증 (프리미엄 UI)
  // ============================================================

  const renderStep4_Validation = () => (
    <Fade in timeout={500}>
      <Box>
        {validationLoading ? (
          <Box sx={{ 
            textAlign: 'center', 
            py: 8,
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            borderRadius: 3
          }}>
            <Box sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: PREMIUM_COLORS.success.gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
              boxShadow: `0 8px 32px ${alpha(PREMIUM_COLORS.success.main, 0.3)}`
            }}>
              <CircularProgress size={40} sx={{ color: 'white' }} />
            </Box>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              데이터 검증 중...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              가격 데이터의 무결성을 확인하고 있습니다
            </Typography>
          </Box>
        ) : validationResult ? (
          <>
            {/* 검증 결과 헤더 */}
            <Box sx={{ 
              p: 3,
              background: validationResult.isValid 
                ? PREMIUM_COLORS.success.gradient 
                : validationResult.errorCount > 0 
                  ? PREMIUM_COLORS.error.gradient 
                  : PREMIUM_COLORS.warning.gradient,
              borderRadius: 3,
              color: 'white',
              mb: 3,
              position: 'relative',
              overflow: 'hidden'
            }}>
              <Box sx={{
                position: 'absolute',
                top: -30,
                right: -30,
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)'
              }} />
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {validationResult.isValid ? (
                    <CheckIcon sx={{ fontSize: 32 }} />
                  ) : validationResult.errorCount > 0 ? (
                    <ErrorIcon sx={{ fontSize: 32 }} />
                  ) : (
                    <WarningIcon sx={{ fontSize: 32 }} />
                  )}
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {validationResult.isValid
                      ? '✓ 검증 통과'
                      : validationResult.errorCount > 0
                        ? '✗ 검증 실패'
                        : '⚠ 경고 발견'}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {validationResult.totalRecordsChecked.toLocaleString()}개 레코드 검증 완료
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* 검증 통계 */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={4}>
                <GlassCard sx={{ 
                  background: validationResult.errorCount > 0 
                    ? 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)' 
                    : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)'
                }}>
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <ErrorIcon sx={{ 
                      fontSize: 32, 
                      color: validationResult.errorCount > 0 ? PREMIUM_COLORS.error.main : '#9ca3af',
                      mb: 1
                    }} />
                    <Typography variant="h3" fontWeight={700} color={validationResult.errorCount > 0 ? 'error' : 'text.secondary'}>
                      {validationResult.errorCount}
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>에러</Typography>
                  </CardContent>
                </GlassCard>
              </Grid>
              <Grid item xs={4}>
                <GlassCard sx={{ 
                  background: validationResult.warningCount > 0 
                    ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' 
                    : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)'
                }}>
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <WarningIcon sx={{ 
                      fontSize: 32, 
                      color: validationResult.warningCount > 0 ? PREMIUM_COLORS.warning.main : '#9ca3af',
                      mb: 1
                    }} />
                    <Typography variant="h3" fontWeight={700} color={validationResult.warningCount > 0 ? 'warning.main' : 'text.secondary'}>
                      {validationResult.warningCount}
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>경고</Typography>
                  </CardContent>
                </GlassCard>
              </Grid>
              <Grid item xs={4}>
                <GlassCard sx={{ 
                  background: validationResult.isValid 
                    ? 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)' 
                    : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)'
                }}>
                  <CardContent sx={{ textAlign: 'center', py: 3 }}>
                    <CheckIcon sx={{ 
                      fontSize: 32, 
                      color: validationResult.isValid ? PREMIUM_COLORS.success.main : '#9ca3af',
                      mb: 1
                    }} />
                    <Typography variant="h3" fontWeight={700} color={validationResult.isValid ? 'success.main' : 'text.secondary'}>
                      {validationResult.totalRecordsChecked - validationResult.errorCount - validationResult.warningCount}
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>정상</Typography>
                  </CardContent>
                </GlassCard>
              </Grid>
            </Grid>

            {/* 이슈 목록 */}
            {validationResult.issues.length > 0 && (
              <GlassCard>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                    발견된 이슈 ({validationResult.issues.length}개)
                  </Typography>
                  <Paper sx={{ maxHeight: 250, overflow: 'auto', bgcolor: '#f9fafb' }}>
                    <List dense>
                      {validationResult.issues.slice(0, 50).map((issue, idx) => (
                        <ListItem 
                          key={idx}
                          sx={{
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:last-child': { borderBottom: 'none' }
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {issue.severity === 'error' ? (
                              <ErrorIcon color="error" fontSize="small" />
                            ) : issue.severity === 'warning' ? (
                              <WarningIcon color="warning" fontSize="small" />
                            ) : (
                              <InfoIcon color="info" fontSize="small" />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={issue.message}
                            secondary={issue.priceId || issue.modelId}
                            primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItem>
                      ))}
                      {validationResult.issues.length > 50 && (
                        <ListItem>
                          <ListItemText
                            primary={`... 외 ${validationResult.issues.length - 50}개 이슈`}
                            sx={{ textAlign: 'center' }}
                            primaryTypographyProps={{ color: 'text.secondary', fontStyle: 'italic' }}
                          />
                        </ListItem>
                      )}
                    </List>
                  </Paper>
                </CardContent>
              </GlassCard>
            )}
          </>
        ) : (
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            검증 결과를 불러올 수 없습니다.
          </Alert>
        )}
      </Box>
    </Fade>
  );

  // ============================================================
  // 🎨 Step 5: 최종 확인 및 복구 실행 (프리미엄 UI)
  // ============================================================

  const renderStep5_ConfirmRestore = () => (
    <Fade in timeout={500}>
      <Box>
        {/* 복구 완료 */}
        {restoreResult && (
          <Box sx={{ 
            p: 4,
            background: restoreResult.success ? PREMIUM_COLORS.success.gradient : PREMIUM_COLORS.error.gradient,
            borderRadius: 3,
            color: 'white',
            textAlign: 'center',
            mb: 3
          }}>
            <Box sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2
            }}>
              {restoreResult.success ? (
                <CheckIcon sx={{ fontSize: 48 }} />
              ) : (
                <ErrorIcon sx={{ fontSize: 48 }} />
              )}
            </Box>
            <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
              {restoreResult.success ? '🎉 복구 완료!' : '복구 실패'}
            </Typography>
            {restoreResult.success ? (
              <Box>
                <Typography variant="body1" sx={{ mb: 2, opacity: 0.9 }}>
                  총 {restoreResult.statistics.totalRestored.toLocaleString()}개 레코드가 성공적으로 복구되었습니다
                </Typography>
                <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
                  <Chip label={`추가: ${restoreResult.statistics.added}`} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                  <Chip label={`업데이트: ${restoreResult.statistics.updated}`} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                  <Chip label={`삭제: ${restoreResult.statistics.deleted}`} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                </Stack>
                {restoreResult.preRestoreBackupId && (
                  <Typography variant="body2" sx={{ mt: 2, opacity: 0.8 }}>
                    🔒 복구 전 백업 ID: {restoreResult.preRestoreBackupId}
                  </Typography>
                )}
              </Box>
            ) : (
              restoreResult.errors?.map((e, i) => (
                <Typography key={i} variant="body2">{e.error}</Typography>
              ))
            )}
          </Box>
        )}

        {/* 복구 진행 중 */}
        {restoring && restoreProgress && (
          <Box sx={{ 
            p: 4,
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            borderRadius: 3,
            textAlign: 'center',
            mb: 3
          }}>
            <Box sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: PREMIUM_COLORS.primary.gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
              boxShadow: `0 8px 32px ${alpha(PREMIUM_COLORS.primary.main, 0.3)}`
            }}>
              <CircularProgress size={40} sx={{ color: 'white' }} />
            </Box>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              {restoreProgress.message}
            </Typography>
            <Box sx={{ px: 4, mb: 2 }}>
              <LinearProgress
                variant={restoreProgress.totalRecords > 0 ? 'determinate' : 'indeterminate'}
                value={restoreProgress.totalRecords > 0
                  ? (restoreProgress.processedRecords / restoreProgress.totalRecords) * 100
                  : 0}
                sx={{ 
                  height: 10, 
                  borderRadius: 5,
                  bgcolor: alpha(PREMIUM_COLORS.primary.main, 0.1),
                  '& .MuiLinearProgress-bar': {
                    background: PREMIUM_COLORS.primary.gradient,
                    borderRadius: 5
                  }
                }}
              />
            </Box>
            {restoreProgress.totalRecords > 0 && (
              <Typography variant="body2" color="text.secondary">
                {restoreProgress.processedRecords.toLocaleString()} / {restoreProgress.totalRecords.toLocaleString()}
              </Typography>
            )}
          </Box>
        )}

        {/* 복구 전 설정 */}
        {!restoreResult && !restoring && (
          <>
            {/* 경고 배너 */}
            <Box sx={{ 
              p: 2.5,
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              borderRadius: 2,
              border: '1px solid',
              borderColor: alpha('#f59e0b', 0.3),
              mb: 3
            }}>
              <Stack direction="row" alignItems="flex-start" spacing={2}>
                <Box sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <WarningIcon sx={{ color: 'white' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} color="warning.dark">
                    ⚠️ 중요 안내
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    복구 작업은 현재 가격 데이터를 변경합니다. 
                    이 작업은 되돌릴 수 없으므로 신중하게 진행해주세요.
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* 복구 요약 */}
            <GlassCard sx={{ mb: 3 }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <TimelineIcon sx={{ color: PREMIUM_COLORS.primary.main }} />
                  <Typography variant="subtitle1" fontWeight={700}>
                    복구 요약
                  </Typography>
                </Stack>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 2, bgcolor: '#f9fafb', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">스냅샷</Typography>
                      <Typography variant="body1" fontWeight={600}>{snapshot.description}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 2, bgcolor: '#f9fafb', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">생성일</Typography>
                      <Typography variant="body1" fontWeight={600}>{formatDate(snapshot.createdAt)}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 2, bgcolor: '#f9fafb', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">복구 범위</Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {filter.brand === 'all' ? '전체' : filter.brand}
                        {' / '}
                        {filter.deviceType === 'all' ? '전체' : filter.deviceType}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper sx={{ p: 2, bgcolor: '#f9fafb', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">예상 변경</Typography>
                      <Stack direction="row" spacing={1}>
                        {diffResult ? (
                          <>
                            <Chip size="small" label={`+${diffResult.summary.added}`} color="success" />
                            <Chip size="small" label={`-${diffResult.summary.removed}`} color="error" />
                            <Chip size="small" label={`~${diffResult.summary.modified}`} color="warning" />
                          </>
                        ) : '-'}
                      </Stack>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </GlassCard>

            {/* 복구 옵션 */}
            <GlassCard>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <SecurityIcon sx={{ color: PREMIUM_COLORS.primary.main }} />
                  <Typography variant="subtitle1" fontWeight={700}>
                    복구 옵션
                  </Typography>
                </Stack>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  복구 모드
                </Typography>
                
                <Stack spacing={1.5} sx={{ mb: 3 }}>
                  {([
                    { 
                      value: 'replace', 
                      label: '전체 교체', 
                      desc: '선택 범위의 기존 데이터를 삭제하고 스냅샷 데이터로 교체',
                      icon: <SwapIcon />
                    },
                    { 
                      value: 'merge', 
                      label: '병합', 
                      desc: '기존 데이터를 유지하고 스냅샷 데이터로 업데이트 (삭제 없음)',
                      icon: <DataIcon />
                    }
                  ] as const).map((mode) => {
                    const isSelected = restoreMode === mode.value;
                    
                    return (
                      <Paper
                        key={mode.value}
                        onClick={() => setRestoreMode(mode.value)}
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          border: '2px solid',
                          borderColor: isSelected ? PREMIUM_COLORS.primary.main : 'transparent',
                          background: isSelected 
                            ? alpha(PREMIUM_COLORS.primary.main, 0.05)
                            : '#f9fafb',
                          borderRadius: 2,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: alpha(PREMIUM_COLORS.primary.main, 0.5)
                          }
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Radio 
                            checked={isSelected}
                            sx={{ '&.Mui-checked': { color: PREMIUM_COLORS.primary.main } }}
                          />
                          <Box sx={{ 
                            color: isSelected ? PREMIUM_COLORS.primary.main : '#6b7280'
                          }}>
                            {mode.icon}
                          </Box>
                          <Box>
                            <Typography variant="body1" fontWeight={isSelected ? 600 : 500}>
                              {mode.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {mode.desc}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Paper
                  onClick={() => setCreateBackupBefore(!createBackupBefore)}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    border: '2px solid',
                    borderColor: createBackupBefore ? PREMIUM_COLORS.success.main : 'transparent',
                    background: createBackupBefore 
                      ? alpha(PREMIUM_COLORS.success.main, 0.05)
                      : '#f9fafb',
                    borderRadius: 2,
                    transition: 'all 0.2s ease'
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Checkbox 
                      checked={createBackupBefore}
                      sx={{ '&.Mui-checked': { color: PREMIUM_COLORS.success.main } }}
                    />
                    <ShieldIcon sx={{ color: createBackupBefore ? PREMIUM_COLORS.success.main : '#6b7280' }} />
                    <Box>
                      <Typography variant="body1" fontWeight={createBackupBefore ? 600 : 500}>
                        복구 전 현재 데이터 백업
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        복구 실행 전 현재 가격 데이터를 자동으로 백업합니다 (권장)
                      </Typography>
                    </Box>
                    <Chip 
                      size="small" 
                      label="권장" 
                      color="success" 
                      sx={{ ml: 'auto', display: createBackupBefore ? 'flex' : 'none' }} 
                    />
                  </Stack>
                </Paper>
              </CardContent>
            </GlassCard>
          </>
        )}
      </Box>
    </Fade>
  );

  // ============================================================
  // 🎨 메인 렌더링
  // ============================================================

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: return renderStep1_SnapshotInfo();
      case 1: return renderStep2_SelectScope();
      case 2: return renderStep3_DiffPreview();
      case 3: return renderStep4_Validation();
      case 4: return renderStep5_ConfirmRestore();
      default: return null;
    }
  };

  const canProceed = () => {
    if (loading || diffLoading || validationLoading) return false;
    if (activeStep === 3 && validationResult && !validationResult.isValid) return false;
    return true;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 4,
          background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          minHeight: '70vh'
        }
      }}
    >
      {/* 프리미엄 헤더 */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        color: 'white',
        p: 3,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Box sx={{
          position: 'absolute',
          top: -50,
          right: -50,
          width: 150,
          height: 150,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)'
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: -30,
          left: '20%',
          width: 100,
          height: 100,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.03)'
        }} />
        
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ position: 'relative' }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              background: PREMIUM_COLORS.primary.gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 8px 24px ${alpha(PREMIUM_COLORS.primary.main, 0.4)}`
            }}>
              <RestoreIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                가격 데이터 복구 마법사
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                5단계 안전한 복구 프로세스
              </Typography>
            </Box>
          </Stack>
          <IconButton 
            onClick={handleClose} 
            disabled={restoring}
            sx={{ 
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {/* 에러 표시 */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 3, borderRadius: 2 }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* 프리미엄 Stepper */}
        <PremiumStepper 
          activeStep={activeStep} 
          steps={WIZARD_STEPS} 
          isMobile={isSmallMobile}
        />

        {/* 스텝 컨텐츠 */}
        <Box sx={{ minHeight: 350 }}>
          {renderStepContent()}
        </Box>
      </DialogContent>

      {/* 프리미엄 푸터 */}
      <Box sx={{ 
        p: 2.5, 
        borderTop: '1px solid',
        borderColor: 'divider',
        background: '#f8fafc'
      }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Button
            onClick={handleClose}
            disabled={restoring}
            sx={{ 
              px: 3,
              color: '#64748b',
              '&:hover': { bgcolor: '#f1f5f9' }
            }}
          >
            취소
          </Button>

          <Stack direction="row" spacing={2}>
            <Button
              onClick={handleBack}
              disabled={activeStep === 0 || restoring || !!restoreResult}
              startIcon={<BackIcon />}
              sx={{ 
                px: 3,
                color: '#64748b',
                borderColor: '#e2e8f0',
                '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' }
              }}
              variant="outlined"
            >
              이전
            </Button>

            {activeStep < WIZARD_STEPS.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!canProceed()}
                endIcon={diffLoading || validationLoading ? <CircularProgress size={16} color="inherit" /> : <NextIcon />}
                sx={{
                  px: 4,
                  background: PREMIUM_COLORS.primary.gradient,
                  boxShadow: `0 4px 16px ${alpha(PREMIUM_COLORS.primary.main, 0.3)}`,
                  '&:hover': {
                    background: PREMIUM_COLORS.primary.gradient,
                    boxShadow: `0 6px 20px ${alpha(PREMIUM_COLORS.primary.main, 0.4)}`
                  }
                }}
              >
                {diffLoading ? 'Diff 계산 중...' : validationLoading ? '검증 중...' : '다음'}
              </Button>
            ) : (
              !restoreResult && (
                <Button
                  variant="contained"
                  onClick={handleRestore}
                  disabled={restoring || !canProceed()}
                  startIcon={restoring ? <CircularProgress size={16} color="inherit" /> : <ExecuteIcon />}
                  sx={{
                    px: 4,
                    background: PREMIUM_COLORS.error.gradient,
                    boxShadow: `0 4px 16px ${alpha(PREMIUM_COLORS.error.main, 0.3)}`,
                    '&:hover': {
                      background: PREMIUM_COLORS.error.gradient,
                      boxShadow: `0 6px 20px ${alpha(PREMIUM_COLORS.error.main, 0.4)}`
                    }
                  }}
                >
                  {restoring ? '복구 중...' : '복구 실행'}
                </Button>
              )
            )}

            {restoreResult?.success && (
              <Button
                variant="contained"
                onClick={() => onClose(true)}
                startIcon={<CheckIcon />}
                sx={{
                  px: 4,
                  background: PREMIUM_COLORS.success.gradient,
                  boxShadow: `0 4px 16px ${alpha(PREMIUM_COLORS.success.main, 0.3)}`,
                  '&:hover': {
                    background: PREMIUM_COLORS.success.gradient,
                    boxShadow: `0 6px 20px ${alpha(PREMIUM_COLORS.success.main, 0.4)}`
                  }
                }}
              >
                완료
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
    </Dialog>
  );
};

export default PriceRestoreWizard;
